import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FlightApiStatus } from '@flugwettrennen/shared';
import { api } from '../api';
import { dateTime } from '../lib/format';

/**
 * Technische Einstellungen der Installation. Zurzeit: wie viel vom
 * Monatskontingent der Flugdatenquelle noch übrig ist.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<FlightApiStatus | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.settings
      .flightApi()
      .then((r) => setStatus(r.status))
      .catch(() => setFailed(true));
  }, []);

  /**
   * Maßgeblich sind die API-Units, nicht die Anfragen: ein Abruf kostet je nach
   * Endpunkt mehrere Units, der Anfragenzähler läuft deshalb viel langsamer
   * leer. Wer auf ihn schaut, sieht noch Luft, wenn längst nichts mehr geht –
   * genau so ist das Kontingent schon einmal ohne Vorwarnung ausgegangen.
   */
  const budget =
    status && status.unitsRemaining !== null
      ? { remaining: status.unitsRemaining, limit: status.unitsLimit, units: true }
      : status && status.remaining !== null
        ? { remaining: status.remaining, limit: status.limit, units: false }
        : null;
  const share = budget?.limit
    ? Math.max(0, Math.min(1, budget.remaining / budget.limit))
    : null;

  return (
    <div className="container page stack">
      <div>
        <div className="eyebrow">{t('nav.settings')}</div>
        <h1>{t('appSettings.title')}</h1>
        <p className="muted" style={{ marginTop: -4 }}>{t('appSettings.subtitle')}</p>
      </div>

      <div className="card stack" data-testid="quota-card">
        <div className="eyebrow">{t('appSettings.flightApi')}</div>

        {failed && <div className="notice notice-error">{t('errors.generic')}</div>}
        {!status && !failed && <p className="muted">{t('common.loading')}</p>}

        {status && (
          <>
            <div className="field">
              <label>{t('appSettings.provider')}</label>
              <div className="mono" data-testid="quota-provider">
                {status.provider}
                {status.gateway && ` · ${t(`appSettings.gateway.${status.gateway}`)}`}
              </div>
            </div>

            {budget ? (
              <div className="field">
                <label>
                  {budget.units ? t('appSettings.remainingUnits') : t('appSettings.remaining')}
                </label>
                <div className="mono" data-testid="quota-remaining">
                  {budget.remaining}
                  {budget.limit !== null && ` ${t('appSettings.ofLimit', { limit: budget.limit })}`}
                </div>
                {share !== null && (
                  <div className="progress-bar" style={{ marginTop: 6 }}>
                    <span style={{ width: `${Math.round(share * 100)}%` }} />
                  </div>
                )}
                {budget.units && (
                  <p className="muted" style={{ fontSize: '0.82rem', margin: '4px 0 0' }}>
                    {status.unitsEstimated
                      ? t('appSettings.unitsEstimatedHint')
                      : t('appSettings.unitsHint')}
                  </p>
                )}
              </div>
            ) : (
              <p className="muted" data-testid="quota-unknown">
                {status.provider === 'mock' ? t('appSettings.mockHint') : t('appSettings.noQuota')}
              </p>
            )}

            {budget?.units && status.remaining !== null && (
              <div className="field">
                <label>{t('appSettings.requests')}</label>
                <div className="mono" data-testid="quota-requests">
                  {status.remaining}
                  {status.limit !== null && ` ${t('appSettings.ofLimit', { limit: status.limit })}`}
                </div>
              </div>
            )}

            {status.resetAt && (
              <div className="field">
                <label>{t('appSettings.reset')}</label>
                <div className="mono">{dateTime(status.resetAt)}</div>
              </div>
            )}

            <div className="field">
              <label>{t('appSettings.calls')}</label>
              <div className="mono" data-testid="quota-calls">
                {status.callsThisMonth} · {status.month}
              </div>
              <p className="muted" style={{ fontSize: '0.82rem', margin: '4px 0 0' }}>
                {t('appSettings.callsHint')}
              </p>
            </div>

            <div className="field">
              <label>{t('appSettings.cache')}</label>
              <div className="mono">
                {t('appSettings.cacheValue', { seconds: status.cacheTtlSeconds })}
              </div>
            </div>

            <p className="muted" style={{ fontSize: '0.82rem', margin: 0 }}>
              {t('appSettings.hint')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
